import { Component, ChangeDetectionStrategy, input, computed, inject, effect } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavItemListComponent } from './nav-item-list.component';
import { SidebarNavStateService } from './sidebar-nav-state.service';
import { NavConfigEntry, NavItem, isNavSection } from './sidebar-nav.types';
import { ZagIcon } from '../icon/icon';

/**
 * Sidebar navigation component
 *
 * Renders a collapsible sidebar navigation menu with support for:
 * - Section-based flat navigation (no collapsible parent items)
 * - Collapsed/expanded states (collapsed → icon-only + section separators)
 * - Mobile responsive drawer
 * - Role-based filtering (delegated to the app layer before passing entries)
 */
@Component({
  selector: 'zag-sidebar-nav',
  imports: [RouterModule, TranslatePipe, NavItemListComponent, ZagIcon],
  template: `
    <!-- Backdrop for mobile drawer -->
    @if (isMobile() && navState.isMobileMenuOpen()) {
      <div class="zag-sidebar-nav__backdrop" (click)="navState.closeMobileMenu()" aria-hidden="true"></div>
    }

    <!-- Sidebar navigation -->
    <nav class="zag-sidebar-nav" [attr.aria-label]="'navigation.mainNavigation' | translate">
      <!-- Navigation items list -->
      <ul class="zag-sidebar-nav__list">
        <zag-nav-item-list [entries]="filteredEntries()" [collapsed]="effectiveCollapsed()" />
      </ul>

      <!-- Settings link (only shown if user has permission) -->
      @if (showSettings()) {
        <div class="zag-sidebar-nav__footer">
          <a
            routerLink="/settings"
            routerLinkActive="zag-sidebar-nav__settings--active"
            class="zag-sidebar-nav__settings"
            [attr.aria-label]="'navigation.settings' | translate"
          >
            <span zagIcon="settings"></span>
            @if (!effectiveCollapsed()) {
              <span class="zag-sidebar-nav__settings-label">{{ 'navigation.settings' | translate }}</span>
            }
          </a>
        </div>
      }

      <!-- Profile section (at bottom) -->
      <div class="zag-sidebar-nav__bottom">
        <!-- Profile slot -->
        <div class="zag-sidebar-nav__profile">
          <ng-content select="[slot=profile]" />
        </div>

        <!-- Toggle button (only on desktop, not tablet/mobile) -->
        @if (!isTablet() && !isMobile()) {
          <button
            type="button"
            class="zag-sidebar-nav__toggle"
            (click)="navState.toggleCollapsed()"
            [attr.aria-label]="(effectiveCollapsed() ? 'navigation.expandSidebar' : 'navigation.collapseSidebar') | translate"
            [attr.aria-expanded]="!effectiveCollapsed()"
          >
            <span [zagIcon]="effectiveCollapsed() ? 'chevron-right' : 'chevron-left'"></span>
          </button>
        }
      </div>
    </nav>
  `,
  styleUrl: './sidebar-nav.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-sidebar-nav-container]': 'true',
    '[class.zag-sidebar-nav-container--collapsed]': 'effectiveCollapsed()',
    '[class.zag-sidebar-nav-container--mobile]': 'isMobile()',
    '[class.zag-sidebar-nav-container--mobile-open]': 'isMobile() && navState.isMobileMenuOpen()',
  },
})
export class SidebarNavComponent {
  readonly navState = inject(SidebarNavStateService);
  private readonly router = inject(Router);
  private readonly breakpointObserver = inject(BreakpointObserver);

  // Inputs
  /** Navigation config entries (pre-filtered by the app layer) */
  readonly entries = input.required<NavConfigEntry[]>();
  /** Whether to show the settings link in the footer (default: true for backwards compatibility) */
  readonly showSettings = input<boolean>(true);

  // Reactive breakpoint signals
  private readonly breakpointState = toSignal(
    this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium]),
    { initialValue: null }
  );

  // Computed: Is mobile viewport?
  readonly isMobile = computed(() => {
    this.breakpointState(); // Track breakpoint changes
    return this.breakpointObserver.isMatched([Breakpoints.XSmall, Breakpoints.Small]);
  });

  // Computed: Is tablet viewport?
  readonly isTablet = computed(() => {
    this.breakpointState(); // Track breakpoint changes
    return this.breakpointObserver.isMatched(Breakpoints.Medium);
  });

  // Computed: Effective collapsed state (always collapsed on tablet, controlled on desktop)
  readonly effectiveCollapsed = computed(() => {
    return this.isMobile() || this.isTablet() || this.navState.isCollapsed();
  });

  // Computed: Filter empty sections out of the entries (sections with all items filtered away)
  readonly filteredEntries = computed(() => this.filterEmptySections(this.entries()));

  constructor() {
    // Close mobile menu on navigation
    effect(() => {
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
        if (this.isMobile()) {
          this.navState.closeMobileMenu();
        }
      });
    });
  }

  /**
   * Remove sections that have no items (after app-layer permission filtering).
   */
  private filterEmptySections(entries: NavConfigEntry[]): NavConfigEntry[] {
    return entries.filter((entry): boolean => {
      if (isNavSection(entry)) {
        return entry.items.length > 0;
      }
      return true;
    });
  }

  /**
   * Whether the sidebar has any visible content (for hiding sidebar if empty).
   */
  readonly hasContent = computed(() => this.filteredEntries().length > 0 || this.showSettings());

  /**
   * Collect all NavItems from entries (primary items + section items flattened).
   * Used externally to check if the sidebar has any visible nav items.
   */
  static flattenEntries(entries: NavConfigEntry[]): NavItem[] {
    const items: NavItem[] = [];
    for (const entry of entries) {
      if (isNavSection(entry)) {
        items.push(...entry.items);
      } else {
        items.push(entry);
      }
    }
    return items;
  }
}
